import { type FC, useContext, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { Doc } from "sharedb/lib/client";

import {
  INSTRUCTOR_SHARED_ACCESS_ID,
  LINKED_SECTION_SEEDING_ACCESS_ID,
} from "../../shared/access-id";
import type { BlockType, FieldType, PathType, WorksheetType } from "../../shared/types/worksheet";
import { DATA_PATH } from "../../shared/worksheets/constants";
import { Field } from "../components/Field";

import { t } from "i18n/i18n";
import { Description, Dialog } from "mds/components/Dialog";
import { DocViewContext, type DocViewContextType } from "providers/DocViewProvider";

type TemplateBlockModalProps = {
  open: boolean;
  onClose: () => void;
  doc: Doc<WorksheetType>;
  block: BlockType;
  versionType: "instructor" | "template";
};

export const TemplateBlockModal: FC<TemplateBlockModalProps> = ({
  open,
  onClose,
  doc,
  block,
  versionType,
}) => {
  const docViewCtx = useContext(DocViewContext);
  const [blockHeight, setBlockHeight] = useState<number>(0);
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (ref.current) {
      setBlockHeight(ref.current.clientHeight);
    }
  }, [open, block.id]);

  const instructorAccessIdPath = useMemo(
    () => [DATA_PATH, INSTRUCTOR_SHARED_ACCESS_ID] as PathType,
    [],
  );

  const blockIndex = useMemo(
    () => doc.data.b.findIndex((b) => b.id === block.id),
    [doc.data.b, block.id],
  );
  const fieldPaths = useMemo<PathType[]>(() => {
    if (blockIndex < 0) {
      return [];
    }
    return block.f.map((_, index) => ["b", String(blockIndex), "f", String(index)] as PathType);
  }, [block.f, blockIndex]);

  const modalContext = useMemo<DocViewContextType>(() => {
    return {
      ...docViewCtx,
      readOnly: true,
      canInteractAsStudent: false,
      isAuthoringPage: false,
      showResponses: false,
      isFeaturing: false,
      accessId: versionType === "template" ? LINKED_SECTION_SEEDING_ACCESS_ID : "",
    };
  }, [docViewCtx, versionType]);

  if (blockIndex < 0) {
    return null;
  }

  return (
    <Dialog
      open={open}
      title={t(`worksheet.template_block_modal.${versionType}.title`)}
      onClose={onClose}
    >
      <Description as="div" className="italic">
        {t(`worksheet.template_block_modal.${versionType}.subtitle`)}
      </Description>
      <DocViewContext.Provider value={modalContext}>
        <div className="mt-4 overflow-auto" ref={ref}>
          <div className="page-block-width">
            {(block.f as FieldType[]).map((field, index) => {
              // Field internally keys based on accessId+field.id in some paths; keep this stable per render.
              return (
                <Field
                  accessIdPath={instructorAccessIdPath}
                  blockHeight={blockHeight}
                  blockId={block.id}
                  blockType={block.t}
                  doc={doc}
                  fieldPath={fieldPaths[index]}
                  key={`${block.id}-${field.id}`}
                  responseAccessIds={[]}
                />
              );
            })}
          </div>
        </div>
      </DocViewContext.Provider>
    </Dialog>
  );
};
